import React, { useMemo, useState } from 'react';
import { Download, Receipt, Search, X } from 'lucide-react';
import { ExpenseCategory, TaxExpense, TaxExpenseInput } from '../../../types/database';
import { CATEGORY_OPTIONS, getExpenseCategory, taxYearMonthOptions } from '../../../lib/expense-categories';
import { formatCurrency, formatMonth, formatWholePounds } from '../../../lib/format';
import { TAX_YEAR_MONTHS, calendarYearForMonth, formatTaxYear, taxYearMonthIndex } from '../../../utils/taxUtils';
import {
  useAddTaxExpenseMutation,
  useDeleteTaxExpenseMutation,
  useTaxExpensesQuery,
  useUpdateTaxExpenseMutation,
} from '../../../hooks/useTaxExpensesQuery';
import { Card, CardBody, CardHeader } from '../../ui/card';
import { Alert } from '../../ui/alert';
import { Button } from '../../ui/button';
import { EmptyState } from '../../ui/empty-state';
import { Input } from '../../ui/input';
import { Select } from '../../ui/select';
import { Skeleton } from '../../ui/skeleton';
import { Stat } from '../../ui/stat';
import { ExpenseForm } from './ExpenseForm';
import { ExpenseList } from './ExpenseList';
import { ExpensesByMonthChart } from './ExpensesByMonthChart';
import { ExpensesByCategory } from './ExpensesByCategory';

type SortKey = 'month-asc' | 'month-desc' | 'amount-desc' | 'name-asc';

const SORTS: { id: SortKey; label: string }[] = [
  { id: 'month-asc', label: 'Month (Apr → Mar)' },
  { id: 'month-desc', label: 'Month (latest first)' },
  { id: 'amount-desc', label: 'Amount (highest first)' },
  { id: 'name-asc', label: 'Name (A → Z)' },
];

const compare: Record<SortKey, (a: TaxExpense, b: TaxExpense) => number> = {
  'month-asc': (a, b) => taxYearMonthIndex(a.month) - taxYearMonthIndex(b.month) || a.id - b.id,
  'month-desc': (a, b) => taxYearMonthIndex(b.month) - taxYearMonthIndex(a.month) || b.id - a.id,
  'amount-desc': (a, b) => b.amount - a.amount,
  'name-asc': (a, b) => a.name.localeCompare(b.name),
};

const downloadCsv = (taxYearStart: number, expenses: TaxExpense[]) => {
  const escape = (value: string) => `"${value.replace(/"/g, '""')}"`;
  const rows = [...expenses]
    .sort(compare['month-asc'])
    .map((e) =>
      [
        escape(formatMonth(calendarYearForMonth(taxYearStart, e.month), e.month)),
        escape(e.name),
        escape(getExpenseCategory(e.category).label),
        e.amount.toFixed(2),
      ].join(',')
    );
  const csv = ['Month,Name,Category,Amount (GBP)', ...rows].join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `expenses-${formatTaxYear(taxYearStart)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

interface ExpensesCardProps {
  taxYearStart: number;
  /** Tax + NI saved by these expenses, shown in the header. */
  taxSaved: number;
  /** True when the £1,000 trading allowance beats the logged expenses. */
  usingTradingAllowance: boolean;
}

export const ExpensesCard: React.FC<ExpensesCardProps> = ({ taxYearStart, taxSaved, usingTradingAllowance }) => {
  const { data: expenses = [], isLoading, error } = useTaxExpensesQuery(taxYearStart);
  const addMutation = useAddTaxExpenseMutation();
  const updateMutation = useUpdateTaxExpenseMutation();
  const deleteMutation = useDeleteTaxExpenseMutation();

  const [editing, setEditing] = useState<TaxExpense | null>(null);
  const [search, setSearch] = useState('');
  const [monthFilter, setMonthFilter] = useState<number | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<ExpenseCategory | null>(null);
  const [sort, setSort] = useState<SortKey>('month-asc');

  const total = useMemo(() => expenses.reduce((sum, e) => sum + e.amount, 0), [expenses]);

  const insights = useMemo(() => {
    const byMonth = TAX_YEAR_MONTHS.map((month) => ({
      month,
      total: expenses.filter((e) => e.month === month).reduce((sum, e) => sum + e.amount, 0),
    })).filter((m) => m.total > 0);
    const top = byMonth.reduce((best, m) => (m.total > best.total ? m : best), { month: 4, total: 0 });
    const largest = expenses.reduce<TaxExpense | null>((best, e) => (!best || e.amount > best.amount ? e : best), null);
    return {
      monthsWithExpenses: byMonth.length,
      average: byMonth.length > 0 ? total / byMonth.length : 0,
      topMonth: { total: top.total, label: formatMonth(calendarYearForMonth(taxYearStart, top.month), top.month) },
      largest: { amount: largest?.amount ?? 0, name: largest?.name ?? '—' },
    };
  }, [expenses, total, taxYearStart]);

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return expenses
      .filter((e) => monthFilter === null || e.month === monthFilter)
      .filter((e) => categoryFilter === null || e.category === categoryFilter)
      .filter((e) => !query || e.name.toLowerCase().includes(query))
      .sort(compare[sort]);
  }, [expenses, search, monthFilter, categoryFilter, sort]);

  const visibleTotal = visible.reduce((sum, e) => sum + e.amount, 0);
  const filtersActive = monthFilter !== null || categoryFilter !== null || search.trim() !== '';

  const clearFilters = () => {
    setSearch('');
    setMonthFilter(null);
    setCategoryFilter(null);
  };

  const handleSubmit = async (input: TaxExpenseInput) => {
    if (editing) {
      await updateMutation.mutateAsync({ id: editing.id, expense: input });
      setEditing(null);
    } else {
      await addMutation.mutateAsync(input);
    }
  };

  const handleDelete = (expense: TaxExpense) => {
    if (editing?.id === expense.id) setEditing(null);
    deleteMutation.mutate({ id: expense.id, taxYearStart });
  };

  const description = expenses.length === 0
    ? 'Allowable business costs reduce your taxable profit'
    : usingTradingAllowance
      ? `${formatCurrency(total)} logged · the £1,000 trading allowance is currently worth more`
      : `${formatCurrency(total)} across ${expenses.length} ${expenses.length === 1 ? 'expense' : 'expenses'} · saving ~${formatCurrency(taxSaved)} in tax`;

  return (
    <Card>
      <CardHeader
        icon={<Receipt />}
        title="Expenses"
        description={description}
        actions={
          expenses.length > 0 && (
            <Button variant="secondary" size="sm" onClick={() => downloadCsv(taxYearStart, expenses)}>
              <Download />
              Export CSV
            </Button>
          )
        }
      />
      <CardBody className="flex flex-col gap-5">
        <ExpenseForm
          taxYearStart={taxYearStart}
          editing={editing}
          onSubmit={handleSubmit}
          onCancelEdit={() => setEditing(null)}
          isSaving={addMutation.isPending || updateMutation.isPending}
        />

        {error && <Alert tone="danger">{error.message || 'Could not load expenses'}</Alert>}
        {deleteMutation.error && <Alert tone="danger">Could not delete: {deleteMutation.error.message}</Alert>}

        {isLoading ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-40 rounded-xl" />
            <Skeleton className="h-24 rounded-xl" />
          </div>
        ) : expenses.length === 0 ? (
          <EmptyState
            icon={<Receipt />}
            title="No expenses yet"
            description="Add things like travel, equipment, phone bills or training. They're deducted from your earnings before tax is worked out."
          />
        ) : (
          <>
            {/* Visualise */}
            <div className="grid gap-5 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
              <div>
                <h3 className="mb-3 text-xs font-medium text-fg-subtle">By month · tap a bar to filter</h3>
                <ExpensesByMonthChart
                  taxYearStart={taxYearStart}
                  expenses={expenses}
                  selectedMonth={monthFilter}
                  onSelectMonth={setMonthFilter}
                />
                <div className="mt-4 grid grid-cols-3 gap-2">
                  <Stat label="Monthly average" value={formatWholePounds(insights.average)} hint={`over ${insights.monthsWithExpenses} ${insights.monthsWithExpenses === 1 ? 'month' : 'months'}`} />
                  <Stat label="Biggest month" value={formatWholePounds(insights.topMonth.total)} hint={insights.topMonth.label} />
                  <Stat label="Largest expense" value={formatWholePounds(insights.largest.amount)} hint={insights.largest.name} />
                </div>
              </div>
              <div>
                <h3 className="mb-2 text-xs font-medium text-fg-subtle">By category</h3>
                <ExpensesByCategory
                  expenses={expenses}
                  selectedCategory={categoryFilter}
                  onSelectCategory={setCategoryFilter}
                />
              </div>
            </div>

            {/* Filter + sort */}
            <div className="flex flex-col gap-3 border-t border-line pt-4">
              <div className="grid grid-cols-2 gap-2 md:grid-cols-[minmax(0,1.5fr)_repeat(3,minmax(0,1fr))]">
                <Input
                  aria-label="Search expenses"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  leading={<Search className="size-4" />}
                  className="h-9 pl-9"
                  containerClassName="col-span-2 md:col-span-1"
                />
                <Select
                  aria-label="Filter by month"
                  className="h-9"
                  value={monthFilter === null ? '' : String(monthFilter)}
                  onChange={(next) => setMonthFilter(next === '' ? null : Number(next))}
                  options={[{ value: '', label: 'All months' }, ...taxYearMonthOptions(taxYearStart)]}
                />
                <Select
                  aria-label="Filter by category"
                  className="h-9"
                  value={categoryFilter ?? ''}
                  onChange={(next) => setCategoryFilter(next === '' ? null : (next as ExpenseCategory))}
                  options={[{ value: '', label: 'All categories' }, ...CATEGORY_OPTIONS]}
                />
                <Select
                  aria-label="Sort expenses"
                  className="h-9"
                  value={sort}
                  onChange={(next) => setSort(next as SortKey)}
                  options={SORTS.map((s) => ({ value: s.id, label: s.label }))}
                  containerClassName="col-span-2 md:col-span-1"
                />
              </div>

              <div className="flex items-center justify-between gap-3 px-1 text-xs text-fg-subtle">
                <span>
                  Showing {visible.length} of {expenses.length} ·{' '}
                  <span className="numeric text-fg-muted">{formatCurrency(visibleTotal)}</span>
                </span>
                {filtersActive && (
                  <Button variant="ghost" size="sm" className="h-7 px-2" onClick={clearFilters}>
                    <X />
                    Clear filters
                  </Button>
                )}
              </div>

              {visible.length === 0 ? (
                <p className="py-8 text-center text-[13px] text-fg-subtle">No expenses match these filters.</p>
              ) : (
                <ExpenseList
                  taxYearStart={taxYearStart}
                  expenses={visible}
                  groupByMonth={sort === 'month-asc' || sort === 'month-desc'}
                  editingId={editing?.id ?? null}
                  onEdit={setEditing}
                  onDelete={handleDelete}
                />
              )}
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
};
